import React, { useState, useEffect } from 'react';
import { View as RNView, Pressable as RNPressable } from 'react-native-web';
import { useParams, useNavigate } from 'react-router-dom';
import CustomText from '../../../components/atoms/CustomText';
import { colors, spacing } from '../../../utils/styling';
import OverviewTab from './tabs/OverviewTab';
import OnboardingTab from './tabs/OnboardingTab';
import PersonalityTab from './tabs/PersonalityTab';
import CheckInsTab from './tabs/CheckInsTab';
import NotesTab from './tabs/NotesTab';
import { useResponsive } from '../../../hooks/useResponsive';
import SvgChevron from '../../../components/icons/SvgChevron';
import { useAccount, useAccountContactsWithTitles } from '../../../lib/salesforce';

// Type-safe components
const View = RNView as unknown as React.FC<React.ComponentProps<typeof RNView>>;
const Pressable = RNPressable as unknown as React.FC<React.ComponentProps<typeof RNPressable>>;

// Mock data for fields not in Salesforce
const MOCK_DATA = {
  status: 'ACTIVE',
};

export default function ClientView() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('OVERVIEW');
  const { isMobile } = useResponsive();

  
  console.log('🔍 DEBUG: Rendering ClientView with clientId:', clientId);
  
  // Fetch client data from Salesforce
  const { data: accountData, isLoading: isAccountLoading, error: accountError } = useAccount(clientId || '');
  const { data: contactsData, isLoading: isContactsLoading } = useAccountContactsWithTitles(clientId || '');

  useEffect(() => {
    console.log('📊 DEBUG: Account Data:', accountData);
    console.log('👥 DEBUG: Contacts Data:', contactsData);
    console.log('⏳ DEBUG: Loading States:', { isAccountLoading, isContactsLoading });
    console.log('❌ DEBUG: Account Error:', accountError);
  }, [accountData, contactsData, isAccountLoading, isContactsLoading, accountError]);

  // Loading state - only wait for account data
  if (isAccountLoading) {
    console.log('⌛ DEBUG: Showing loading state');
    return (
      <View style={styles.container}>
        <CustomText style={styles.loadingText}>Loading client data...</CustomText>
      </View>
    );
  }

  // Error state
  if (accountError) {
    console.log('💥 DEBUG: Showing error state:', accountError);
    return (
      <View style={styles.container}>
        <CustomText style={styles.errorText}>Error loading client data. Please try again.</CustomText>
      </View>
    );
  }

  // Get the account and primary contact, with null checks
  const account = accountData?.records?.[0] || accountData;  // Try both response formats
  const primaryContact = contactsData?.records?.[0];

  console.log('🏢 DEBUG: Raw Account Data:', accountData);
  console.log('🏢 DEBUG: Processed Account:', account);
  console.log('👤 DEBUG: Primary Contact:', primaryContact);

  // Not found state - only check account
  if (!account) {
    console.log('🚫 DEBUG: Account not found');
    return (
      <View style={styles.container}>
        <CustomText style={styles.errorText}>Client not found</CustomText>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        {/* On a phone the name block and the tab row stack, and the tabs wrap, so
            nothing sits past the screen edge (Nina, 2026-10-07 call). */}
        <View style={isMobile ? { ...styles.content, padding: spacing.md } : styles.content}>
          <View style={isMobile ? { ...styles.headerContent, flexWrap: 'wrap' } : styles.headerContent}>
            <View style={isMobile ? { ...styles.headerLeft, flexWrap: 'wrap', gap: spacing.sm, width: '100%', flexShrink: 1 } : styles.headerLeft}>
              <Pressable 
                style={styles.backButton}
                onPress={() => navigate('/advisor')}
              >
                <View style={styles.backIconContainer}>
                  <SvgChevron 
                    color={colors.dark} 
                    width={16} 
                    height={16}
                  />
                </View>
              </Pressable>
              <View>
                <CustomText style={styles.clientName}>{account.Name}</CustomText>
                {account.BillingAddress && (
                  <CustomText style={styles.clientLocation}>
                    {account.BillingAddress.city}, {account.BillingAddress.state}, {account.BillingAddress.country}
                  </CustomText>
                )}
                {!isContactsLoading && primaryContact && (
                  <CustomText style={styles.clientContact}>
                    {primaryContact.Email || 'No email'} • {primaryContact.Phone || 'No phone'}
                  </CustomText>
                )}
              </View>

              <View style={isMobile
                ? { ...styles.tabs, marginLeft: 0, width: '100%', flexWrap: 'wrap', columnGap: spacing.lg, rowGap: 0 }
                : styles.tabs}>
                <Pressable 
                  style={styles.tabButton}
                  onPress={() => setActiveTab('OVERVIEW')}
                >
                  <CustomText style={styles.tabText}>
                    OVERVIEW
                  </CustomText>
                  {activeTab === 'OVERVIEW' && <View style={styles.activeTabIndicator} />}
                </Pressable>
                <Pressable 
                  style={styles.tabButton}
                  onPress={() => setActiveTab('ONBOARDING')}
                >
                  <CustomText style={styles.tabText}>
                    ONBOARDING
                  </CustomText>
                  {activeTab === 'ONBOARDING' && <View style={styles.activeTabIndicator} />}
                </Pressable>
                <Pressable 
                  style={styles.tabButton}
                  onPress={() => setActiveTab('PERSONALITY')}
                >
                  <CustomText style={styles.tabText}>
                    MONEY PERSONALITY
                  </CustomText>
                  {activeTab === 'PERSONALITY' && <View style={styles.activeTabIndicator} />}
                </Pressable>
                <Pressable 
                  style={styles.tabButton}
                  onPress={() => setActiveTab('CHECKINS')}
                >
                  <CustomText style={styles.tabText}>
                    CHECK-INS
                  </CustomText>
                  {activeTab === 'CHECKINS' && <View style={styles.activeTabIndicator} />}
                </Pressable>
                <Pressable 
                  style={styles.tabButton}
                  onPress={() => setActiveTab('NOTES')}
                >
                  <CustomText style={styles.tabText}>
                    NOTES
                  </CustomText>
                  {activeTab === 'NOTES' && <View style={styles.activeTabIndicator} />}
                </Pressable>
              </View>
              

            </View>

            <View style={styles.headerRight}>
              <img
                src={require('../../../components/img/money_personality_full_logo.png')}
                alt="Money Personality Logo"
                style={{ height: 48, width: 'auto', objectFit: 'contain' }}
              />
            </View>
          </View>
        </View>
      </View>

      {/* Main Content */}
      {activeTab === 'OVERVIEW' && <OverviewTab sfAccount={account.Id} clientName={account.Name} />}
      {activeTab === 'ONBOARDING' && <OnboardingTab />}
      {activeTab === 'PERSONALITY' && <PersonalityTab sfAccount={account.Id} activeTab="results" userName={account.Name} />}
      {activeTab === 'CHECKINS' && <CheckInsTab />}
      {activeTab === 'NOTES' && <NotesTab sfAccount={account.Id} />}
    </View>
  );
}

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.gray[100],
    minHeight: '100vh',
  },
  header: {
    backgroundColor: colors.white,
    borderBottom: `1px solid ${colors.gray[200]}`,
    width: '100%',
  },
  content: {
    padding: spacing.xl,
    width: '100%',
    maxWidth: 1200,
    marginHorizontal: 'auto',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.lg,
  },
  backButton: {
    padding: spacing.sm,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,
  backIconContainer: {
    transform: 'rotate(90deg)',
    display: 'flex',
  } as React.CSSProperties,
  clientName: {
    fontSize: 24,
    fontWeight: '600',
    color: colors.dark,
    marginBottom: spacing.xs,
  },
  clientLocation: {
    fontSize: 14,
    color: colors.gray[600],
  },
  clientContact: {
    fontSize: 14,
    color: colors.gray[600],
  },
  headerRight: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  headerButton: {
    backgroundColor: colors.primary,
    padding: `${spacing.sm}px ${spacing.lg}px`,
    borderRadius: 4,
    cursor: 'pointer',
  },
  buttonText: {
    color: colors.white,
    fontSize: 14,
  },
  tabs: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: spacing.xl,
    gap: spacing.xl,
  },
  tabButton: {
    paddingVertical: 16,
    paddingHorizontal: 0,
    position: 'relative',
  } as React.CSSProperties,
  tabText: {
    color: colors.dark,
    fontWeight: '600',
    fontSize: 14,
  },
  activeTabIndicator: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: colors.primary,
  },

  loadingText: {
    fontSize: 16,
    color: colors.gray[600],
    textAlign: 'center',
    marginTop: spacing.xl,
  },
  errorText: {
    fontSize: 16,
    color: colors.danger,
    textAlign: 'center',
    marginTop: spacing.xl,
  },
} as const; 